import { useEffect } from 'react'
import { ClientOnly } from '@/features/shared/components/ClientOnly'
import { type MarketingStoresCarouselData } from '@/features/cms/components/CMS/types'
import styled from 'styled-components'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { StoreTile } from '@/features/cms/components/CMS/components/BrandAmplification/StoreTile'
import { Carousel } from '@shipt/design-system-carousel'
import { BREAKPOINTS, screenSizes } from '@shipt/design-system-themes'
import { spacingPx } from '@/features/shared/theme/tokens'
import chunk from 'lodash/chunk'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { SkeletonLoader } from '@/features/shared/components/SkeletonLoader'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { useMediaQuery } from '@/features/shared/hooks/useMediaQuery'
import { useIsPage } from '@/features/shared/hooks/useIsPage'
import { getRetailerUrl } from '@shared/constants/routes'
import { BotOnly } from '@/features/shared/components/BotOnly'
import { Image } from '@/features/shared/elements/Image/NextImage'

const renderStoreTile = (
  store: {
    id: number
    name: string
    circular_image: string
    background_color?: string
    recently_launched: boolean
  },
  onClick: () => void,
  link?: string,
) => {
  const tile = (
    <StoreTile
      image={store.circular_image}
      name={store.name}
      backgroundColor={store.background_color}
      showNewBadge={store.recently_launched}
    />
  )
  if (link) {
    return (
      <StoreTileWrapper
        key={store.id}
        as="a"
        href={link}
        aria-label={`Find stores like ${store.name} with Target Circle 360, Click to sign in`}
        onClick={onClick}
      >
        {tile}
      </StoreTileWrapper>
    )
  }
  return <StoreTileWrapper key={store.id}>{tile}</StoreTileWrapper>
}

const getStoreLayoutSize = (storeCount: number) => {
  if (storeCount <= 5) return 'small'
  if (storeCount <= 7) return 'medium'
  return 'large'
}

/**
 * @deprecated use the StoresCarousel instead.
 */
export const MarketingStoresCarousel = ({
  content_type_id,
  id,
  data: { store_cards, heading, link } = {},
}: MarketingStoresCarouselData) => {
  const { trackCMSElementClicked, trackCMSElementViewed } = useCMSTrackingEvents()
  const { screenSm: isMobile } = useMediaQuery()
  const { TARGET_CIRCLE_360: isC360LP } = useIsPage()

  const getStoreLink = (storeName: string) => {
    if (!link?.url) return
    if (isC360LP) return `${link.url}&returnTo=${getRetailerUrl(storeName)}`

    return getRetailerUrl(storeName)
  }

  useEffect(() => {
    if (store_cards?.length)
      trackCMSElementViewed({
        content: 'Marketing Stores Near You Carousel',
      })
  }, [trackCMSElementViewed, store_cards])

  if (!store_cards) return null

  const handleClick = (index: number, storeId: number) =>
    trackCMSElementClicked({
      type: 'shelf',
      content: 'Sign in retailer logo',
      display_sublocation: 'retailer_shelf',
      list_index: index,
      store_id: storeId,
    })

  const stores = store_cards.map(({ data }) => {
    return {
      circular_image: data.logo.src,
      name: data.name,
      recently_launched: data.recently_launched,
      id: data.retailer_id,
      background_color: data.background_color,
    }
  })

  const DesktopCarousel = (
    <Carousel
      title={{ content: heading ?? 'Stores near you', isHidden: !heading }}
      hasInlineControls
      layoutConfig={{
        [BREAKPOINTS.SMALL]: {
          slidesPerView: 4,
          slidesPerGroup: 4,
        },
        [BREAKPOINTS.MEDIUM]: {
          slidesPerGroup: 6,
          slidesPerView: 6,
        },
        [BREAKPOINTS.LARGE]: {
          slidesPerView: 8,
          slidesPerGroup: 8,
        },
      }}
    >
      {stores.map((store, index) =>
        renderStoreTile(store, () => handleClick(index, store.id), getStoreLink(store.name)),
      )}
    </Carousel>
  )

  const MobileCarousel = (
    <Carousel
      title={{ content: heading ?? 'Stores near you', isHidden: !heading }}
      hasInlineControls
      className="mobile-carousel"
    >
      {stores.length > 5
        ? chunk(stores, 2).map((tuple) => (
            <Tuple key={tuple[0]?.id}>
              {tuple.map((store, index) =>
                renderStoreTile(
                  store,
                  () => handleClick(index, store.id),
                  getStoreLink(store.name),
                ),
              )}
            </Tuple>
          ))
        : stores.map((store, index) =>
            renderStoreTile(store, () => handleClick(index, store.id), getStoreLink(store.name)),
          )}
    </Carousel>
  )

  const storeLayoutSize = getStoreLayoutSize(stores.length)

  return (
    <Wrapper
      {...getCMSWrapperProps({ content_type_id, id })}
      data-store-layout-size={storeLayoutSize}
    >
      {/* wrapped with ClientOnly since the carousel uses swiper internally and the
          layout logic relies on window object, which is not available during SSR. */}
      <ClientOnly fallback={<SkeletonLoader height={200} />}>
        {isMobile ? MobileCarousel : DesktopCarousel}
      </ClientOnly>
      <BotOnly>
        <h2>{heading ?? 'Stores near you'}</h2>
        <ul>
          {stores.map((store) => (
            <li key={store.id}>
              <a href={getStoreLink(store.name)} aria-label={store.name}>
                <Image src={store.circular_image} alt={store.name} width={100} height={100} />
                {store.name}
              </a>
            </li>
          ))}
        </ul>
      </BotOnly>
    </Wrapper>
  )
}

const Wrapper = styled(GridLayoutContainer)<{
  'data-store-layout-size': 'small' | 'medium' | 'large'
}>`
  width: 100%;
  min-height: 200px;

  @media ${screenSizes.tablet} {
    min-height: 150px;

    &[data-store-layout-size='small'] ul {
      display: flex;
      justify-content: center;
    }
  }

  @media ${screenSizes.smDesktop} {
    --content-max-width: 1280px;

    &[data-store-layout-size='medium'] ul {
      display: flex;
      justify-content: center;
    }
  }

  .mobile-carousel {
    grid-column: full-width;
  }
`

const Tuple = styled.div`
  display: grid;
  flex-grow: 1;
  gap: ${spacingPx('sm')};
  grid-template-rows: 1fr 1fr;
`

const StoreTileWrapper = styled.div`
  flex-grow: 1;
  align-self: start;
  text-align: left;
  width: 100%;
  text-decoration: none;
`
